import { Injectable } from '@angular/core';
import { LoggingService } from '../logging/logic/logging.service';
import { AppParameterService } from '../app-parameter/logic/app-parameter.service';
import { TranslateService } from '@ngx-translate/core';

import * as Leaflet from 'leaflet';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { PositionLog } from 'src/app/_model/position-log';
import { Branch } from 'src/app/_model/branches';
import { Servicejob } from 'src/app/_model/servicejob';
import { ClientStateService } from '../client-state/logic/client-state.service';
import { BranchesService } from '../branches/logic/branches.service';
import { ServicejobsService } from '../servicejobs/logic/servicejobs.service';
import { DataTasksService } from '../data-tasks/data-tasks.service';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import { CarsService } from '../cars/logic/cars.service';
import { AlertService } from '../alert/alert.service';
import { UpdatePositionRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/update-position-remote-action-builder/update-position-remoteaction-builder';
import { AppService } from '../app-service/logic/app.service';
import { CredentialsService } from '../credentials/logic/credentials.service';
import { LocationService } from '../location/location.service';
import { RemoteActionService } from '../remote-action/logic/remote-action.service';
import { LoadingService } from '../loading/loading.service';
import { ImsErrorHandler } from 'src/app/_util/ims-error-handler';
import { TourStopsService } from '../tour-stop/logic/tour-stop.service';

export const iconTemplate = (iconName: string, size: number = 30) => {
  return `<i class="fas fa-${iconName}"  style="font-size:${size}px;"/>"></i>`;
};

export enum MapObjectType {
  USER,
  CAR,
  BRANCH,
  JOB,
}

export enum MapPrecision {
  ADDRESS,
  MANUAL,
  UNKNOWN,
}

export type MapObject = {
  type: MapObjectType;
  marker: Leaflet.Marker;
  text: string;
  object: IMSObject;
  precision?: MapPrecision;
};

@Injectable()
export class MapService {
  // Public Member

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private appParameterService: AppParameterService,
    private clientStateService: ClientStateService,
    private branchService: BranchesService,
    private serviceJobsService: ServicejobsService,
    private tourStopsService: TourStopsService,
    private dataTaskService: DataTasksService,
    private carsService: CarsService,
    private alertService: AlertService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private locationService: LocationService,
    private remoteActionService: RemoteActionService,
    private loadingService: LoadingService,
    private errorHandler: ImsErrorHandler
  ) {}

  public getCurrentlyAvailableMapObjects(
    asyncLoadedObject: (obj: MapObject) => void
  ): MapObject[] {
    const clientState = this.clientStateService.theState.getValue();
    const appParams = this.appParameterService.appParameters$.getValue();
    const mapObjects: MapObject[] = [];

    if (
      clientState?.branchId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-branch'], true)
    ) {
      console.log('JBMapping found branchid', clientState.branchId?.imsId);
      const branchObj = this.loadBranchObject(clientState.branchId);
      if (branchObj != null) {
        mapObjects.push(branchObj);
      }
    }

    if (
      clientState?.serviceJobId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-job'], true)
    ) {
      console.log(
        'JBMapping found serviceJobId',
        clientState.serviceJobId?.imsId
      );
      const jobObj = this.loadServicejobObject(clientState.serviceJobId);
      if (jobObj != null) {
        mapObjects.push(jobObj);
      }
    }
    if (
      clientState?.serviceJobId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-job-branch'], true)
    ) {
      const jobBranchObj = this.loadBranchOfServicejob(
        clientState.serviceJobId
      );
      if (jobBranchObj != null) {
        mapObjects.push(jobBranchObj);
      }
    }

    if (
      clientState?.tourStopId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-tourstops'], true)
    ) {
      console.log('JBMapping found tourStopId', clientState.tourStopId?.imsId);
      const tourJobs = this.serviceJobsService.getServiceJobsForTourStopId(
        clientState.tourStopId
      );

      for (const tourJob of tourJobs) {
        const branchObj = this.loadBranchObject(tourJob.leadingBranch?.id);
        if (branchObj != null) {
          mapObjects.push(branchObj);
        }
      }
    } else if (
      clientState?.tourId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-tour'], true)
    ) {
      console.log('JBMapping found tourId', clientState.tourId?.imsId);
      const tourStops = this.tourStopsService.getTourStopsByTourId(
        clientState.tourId
      );

      for (const tourStop of tourStops) {
        const tourJobs = this.serviceJobsService.getServiceJobsForTourStopId(
          tourStop.id
        );

        for (const tourJob of tourJobs) {
          const branchObj = this.loadBranchObject(tourJob.leadingBranch?.id);
          if (branchObj != null) {
            mapObjects.push(branchObj);
          }
        }
      }
    }
    if (
      clientState?.lastUsedCarId != null &&
      LBSUtils.convertStringBool(appParams['imsmc.map.show-car'], true)
    ) {
      console.log(
        'JBMapping found lastUsedCarId',
        clientState.lastUsedCarId?.imsId
      );
      this.dataTaskService
        .getCarPosition(clientState.lastUsedCarId.imsId)
        .then((pos) => {
          const carMarker = this.convertIMSObjectToMapObject(pos);
          if (carMarker != null) {
            asyncLoadedObject(carMarker);
          }
        });
    }
    // Filter duplicate entries (e.g. branches from cur branch and via tourstops)
    return [
      ...new Map(
        mapObjects.map((mObj) => [
          JSON.stringify([mObj.object.getImsId(), mObj.object?.id?.objRef]),
          mObj,
        ])
      ).values(),
    ];
  }

  public convertIMSObjectToMapObject(obj: IMSObject): MapObject {
    const appParams = this.appParameterService.appParameters$.getValue();

    switch (true) {
      case obj instanceof PositionLog:
        const posLog = obj as PositionLog;
        const car = this.carsService.getCarById(
          this.clientStateService.theState.getValue()?.lastUsedCarId
        );
        console.log('JBMapping car', car);
        return {
          type: MapObjectType.CAR,
          marker: Leaflet.marker(
            Leaflet.latLng(posLog.latitude, posLog.longitude),
            {
              icon: Leaflet.divIcon({
                html: iconTemplate(
                  appParams['imsmc.map.icon.car'],
                  appParams['imsmc.map.icon-size.car']
                ),
                iconSize: [
                  appParams['imsmc.map.icon-size.car'],
                  appParams['imsmc.map.icon-size.car'],
                ],
                className: 'carPosition',
              }),
              zIndexOffset: 1,
            }
          ),
          text: car?.displayLabel,
          object: car,
        };
      case obj instanceof Branch:
        const branch = obj as Branch;

        let branchClassName = 'branchPosition';
        const isBranchPreciseOrManual = LBSUtils.convertStringBool(
          branch.geodataisaddresspreciseormanual,
          false
        );
        const isBranchManual = LBSUtils.convertStringBool(
          branch.geodataismanual,
          false
        );
        if (!isBranchPreciseOrManual) {
          branchClassName += ' inprecisePosition';
        } else if (!isBranchManual) {
          branchClassName += ' possiblyInprecisePosition';
        }

        let branchMarker: Leaflet.Marker;
        if (
          branch.validlatitude != undefined &&
          branch.validlongitude != undefined
        ) {
          branchMarker = Leaflet.marker(
            Leaflet.latLng(branch.validlatitude, branch.validlongitude),
            {
              icon: Leaflet.divIcon({
                html: iconTemplate(
                  appParams['imsmc.map.icon.branch'],
                  appParams['imsmc.map.icon-size.branch']
                ),
                iconSize: [
                  appParams['imsmc.map.icon-size.branch'],
                  appParams['imsmc.map.icon-size.branch'],
                ],
                className: branchClassName,
              }),
              draggable: appParams.getBooleanParameter(
                'imsmc.map.can-edit.branch',
                false
              ),
            }
          );
        }

        return {
          type: MapObjectType.BRANCH,
          marker: branchMarker,
          text: branch.displayLabel,
          object: branch,
          precision: isBranchManual
            ? MapPrecision.MANUAL
            : isBranchPreciseOrManual
            ? MapPrecision.ADDRESS
            : MapPrecision.UNKNOWN,
        };
      case obj instanceof Servicejob:
        const sjob = obj as Servicejob;

        let sjobClassName = 'jobPosition';
        const isJobPreciseOrManual = LBSUtils.convertStringBool(
          sjob.geodataisaddresspreciseormanual,
          false
        );
        const isJobManual = LBSUtils.convertStringBool(
          sjob.geodataismanual,
          false
        );
        if (!isJobPreciseOrManual) {
          sjobClassName += ' inprecisePosition';
        } else if (!isJobManual) {
          sjobClassName += ' possiblyInprecisePosition';
        }

        let sjobMarker: Leaflet.Marker;
        if (
          sjob.validlatitude != undefined &&
          sjob.validlongitude != undefined
        ) {
          sjobMarker = Leaflet.marker(
            Leaflet.latLng(sjob.validlatitude, sjob.validlongitude),
            {
              icon: Leaflet.divIcon({
                html: iconTemplate(
                  appParams['imsmc.map.icon.job'],
                  appParams['imsmc.map.icon-size.job']
                ),
                iconSize: [
                  appParams['imsmc.map.icon-size.job'],
                  appParams['imsmc.map.icon-size.job'],
                ],
                className: sjobClassName,
              }),
              draggable: appParams.getBooleanParameter(
                'imsmc.map.can-edit.job',
                false
              ),
            }
          );
        }

        return {
          type: MapObjectType.JOB,
          marker: sjobMarker,
          text: sjob.displayLabel,
          object: sjob,
          precision: isJobManual
            ? MapPrecision.MANUAL
            : isJobPreciseOrManual
            ? MapPrecision.ADDRESS
            : MapPrecision.UNKNOWN,
        };
      default:
        return null;
    }
  }

  public async updateObjectPosition(object: IMSObject, latlng: Leaflet.LatLng) {
    try {
      await this.loadingService.showLoading();
      const latLng = latlng;
      const rab = new UpdatePositionRemoteActionBuilder(
        object,
        latLng.lat,
        latLng.lng,
        this.branchService,
        this.serviceJobsService,
        this.appService,
        this.appParameterService,
        this.credentialsService,
        this.locationService,
        this.logger
      );
      await this.remoteActionService.sendRemoteAction(
        await rab.generateRemoteAction([])
      );
    } catch (e) {
      await this.errorHandler.handleError(e);
    } finally {
      await this.loadingService.dismissLoading();
    }
  }

  // Private Functions

  public jobDragged(event: Leaflet.DragEndEvent, job: Servicejob) {
    const marker = event.target as Leaflet.Marker;
    this.showDragAlert(
      job,
      () => {
        marker.setLatLng([job.validlatitude, job.validlongitude]);
      },
      () => {
        this.updateObjectPosition(job, marker.getLatLng());
      }
    );
  }

  public branchDragged(event: Leaflet.DragEndEvent, branch: Branch) {
    const marker = event.target as Leaflet.Marker;
    this.showDragAlert(
      branch,
      () => {
        marker.setLatLng([branch.validlatitude, branch.validlongitude]);
      },
      () => {
        this.updateObjectPosition(branch, marker.getLatLng());
      }
    );
  }

  private showDragAlert(
    obj: IMSObject,
    reset: () => void,
    confirm: () => void
  ) {
    this.alertService.showAlert(
      this.translate.instant('COMPONENT.MAP.UPDATE-POSITION.TITLE'),
      obj?.displayLabel,
      this.translate.instant('COMPONENT.MAP.UPDATE-POSITION.MESSAGE'),
      this.translate.instant('COMPONENT.MAP.UPDATE-POSITION.CONFIRM'),
      () => {
        confirm();
      },
      () => {
        reset();
      },
      undefined,
      false,
      undefined,
      this.translate.instant('COMPONENT.MAP.UPDATE-POSITION.CANCEL'),
      undefined
    );
  }

  private loadBranchObject(id: IMSId): MapObject {
    const branch = this.branchService.getBranchById(id);
    if (branch == null) {
      return null;
    }

    return this.convertIMSObjectToMapObject(branch);
  }

  private loadServicejobObject(id: IMSId): MapObject {
    const job = this.serviceJobsService.getServiceJobById(id);
    if (job == null) {
      return null;
    }

    return this.convertIMSObjectToMapObject(job);
  }

  private loadBranchOfServicejob(id: IMSId): MapObject {
    const job = this.serviceJobsService.getServiceJobById(id);
    if (job == null) {
      return null;
    }

    if (job.leadingBranch == null || job.leadingBranch?.id?.imsId == null) {
      return null;
    }

    return this.loadBranchObject(job.leadingBranch.id);
  }
}
